<template>
	<RouterLink v-slot="{ href, navigate }" :to="to" custom>
		<a class="order-card" :class="{ 'order-card--past': past }" :href="href" @click="openOrder(navigate, $event)">
			<span class="order-card__head">
				<span class="order-card__order">
					<Text tag="span" font="var(--order-card-label-font)" color="var(--order-card-label-color)">Order #</Text>
					<Text tag="span" class="order-card__number" font="--text-18-22-med" color="var(--order-card-number-color)">
						{{ order.orderNumber }}
					</Text>
				</span>
				<Badge
					v-if="order.badge"
					class="order-card__badge order-card__badge--narrow"
					:tone="BADGE_TONE[order.badge.tone]"
					size="sm"
				>
					{{ order.badge.label }}
				</Badge>
			</span>
			<span class="order-card__divider" aria-hidden="true" />
			<span class="order-card__medication">
				<Text tag="span" font="--text-14-20-med" :color="KOVA.textSecondary">Medication</Text>
				<Text tag="span" class="order-card__medication-name" font="--text-18-22-med" color="--text-dark-primary">
					{{ order.medicationName }}
				</Text>
			</span>
			<Badge
				v-if="order.badge"
				class="order-card__badge order-card__badge--wide"
				:tone="BADGE_TONE[order.badge.tone]"
				size="md"
			>
				{{ order.badge.label }}
			</Badge>
			<!-- A static glyph from src/assets, inlined so it takes currentColor. -->
			<span class="order-card__arrow" aria-hidden="true" v-html="ARROW_RIGHT_SVG" />
		</a>
	</RouterLink>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, type LocationQuery } from 'vue-router'
import { Badge, Text } from '@simple-life-app/growth-ui-kova'
import { KOVA } from '@/designTokens'
import { openOrder, orderDetailsLocation } from '@/orders/destinations'
import type { OrderListItem } from '@/orders/listModel'
import { ARROW_RIGHT_SVG, BADGE_TONE } from './orderGlyphs'

/**
 * One order on the Orders list (Figma "Order card/ Desktop" 13715:74905, "Order card/ Collapsed" 14254:169712).
 * The whole card is one link to the order's details: a real `<a>` so it opens in a new tab or is bookmarked;
 * the router takes a plain click (`navigate` leaves a modifier click to the browser, as in PortalNav). Where it
 * leads and its click are `src/orders/destinations` seams (`orderDetailsLocation`, `openOrder`). Its
 * accessible name is its text — never an `aria-label` / `title` with the order number (Sentry `ui.click`
 * breadcrumbs record those; docs/architecture/sentry.md).
 *
 * The layout follows the list's own width, the named container `orders` the section sets — not the portal's
 * 1024px switch, since the content column is ~510px at a 1024px window. Below 560px it is the collapsed card
 * ("Order #N" + the 28px badge / divider / "Medication" + name + the arrow tile); from 560px one row ("Order #"
 * over the number, "Medication" over the name, the 36px badge on the right). The badge is drawn in both sizes and
 * CSS shows one, so each layout reads in visual order. Outside an `orders` container the card stays collapsed.
 */

const props = defineProps<{
	order: OrderListItem
	/** A past order: Figma underlines its number in the row layout. */
	past?: boolean
	/** The list's query (`?page=` and the dev params), kept on the details URL so the list under the drawer stays put. */
	query?: LocationQuery
}>()

const to = computed(() => orderDetailsLocation(props.order.id, props.query))
</script>

<style scoped>
/* Collapsed (Figma 14254:169712, 358 wide): padding 20 / 24; the "Order #N" row, a divider and the medication
   row 20 apart; the badge and the arrow tile on the right. */
.order-card {
	--order-card-label-font: var(--text-18-22-med);
	--order-card-label-color: var(--text-dark-primary);
	--order-card-number-color: var(--text-dark-primary);

	display: grid;
	grid-template-areas:
		'head head'
		'divider divider'
		'medication arrow';
	grid-template-columns: minmax(0, 1fr) auto;
	align-items: center;
	gap: var(--size-20) var(--size-12);
	padding: var(--size-20) var(--size-24);
	border-radius: var(--size-24);
	background: var(--background-white);
	color: inherit;
	text-decoration: none;
}

.order-card:focus-visible {
	outline: 2px solid var(--stroke-accent-brand);
	outline-offset: 2px;
}

/* "Order #N" and the badge share the first row; a badge too long for it (a 375px phone, "Delivery failed · Aug 1")
   moves under the number rather than squeezing it until "Order #" and the number break apart. */
.order-card__head {
	display: flex;
	flex-wrap: wrap;
	grid-area: head;
	align-items: center;
	justify-content: space-between;
	gap: var(--size-8) var(--size-12);
	min-width: 0;
}

.order-card__order {
	display: flex;
	grid-area: order;
	min-width: 0;
	white-space: nowrap;
}

.order-card__number,
.order-card__medication-name {
	overflow-wrap: anywhere;
}

.order-card__badge {
	justify-self: end;
}

.order-card .order-card__badge--wide {
	display: none;
}

/* `border/black`, drawn inside the frame as Figma does (KOVA.divider). */
.order-card__divider {
	grid-area: divider;
	height: 1px;
	box-shadow: v-bind('KOVA.divider');
}

.order-card__medication {
	display: flex;
	flex-direction: column;
	grid-area: medication;
	gap: var(--size-4);
	min-width: 0;
}

/* Figma: a 48px `button/secondary/grey` tile, radius 20, the 20px arrow. */
.order-card__arrow {
	display: flex;
	grid-area: arrow;
	justify-self: end;
	align-items: center;
	justify-content: center;
	width: var(--size-48);
	height: var(--size-48);
	border-radius: var(--size-20);
	background: var(--button-secondary-background);
	color: var(--text-dark-primary);
}

/* Row (Figma 13715:74905, 1136 wide): "Order #" (82 wide) and "Medication" columns 32 apart, the badge on the
   right. Figma's card is 92 tall (24 / 32 around a 44px text block); the DS line heights make ours 46, so 23. */
@container orders (min-width: 560px) {
	.order-card {
		--order-card-label-font: var(--text-14-20-med);
		--order-card-label-color: v-bind('KOVA.textSecondary');
		--order-card-number-color: var(--text-accent-brand);

		grid-template-areas: 'order medication badge';
		grid-template-columns: minmax(82px, max-content) minmax(0, 1fr) auto;
		gap: 0 var(--size-32);
		padding: calc(var(--size-24) - 1px) var(--size-32);
	}

	/* The row lays "Order #N" in its own column, so the wrapper steps aside. */
	.order-card__head {
		display: contents;
	}

	.order-card__order {
		flex-direction: column;
		gap: var(--size-4);
	}

	.order-card--past .order-card__number {
		text-decoration: underline;
		text-underline-offset: 0.2em;
	}

	/* Two classes deep: the DS sets the badge's own `display`. */
	.order-card .order-card__badge--narrow,
	.order-card__divider,
	.order-card__arrow {
		display: none;
	}

	.order-card .order-card__badge--wide {
		display: inline-flex;
		grid-area: badge;
	}
}
</style>
